<template>
    <div class="ticimax-filter-item-editor">
        <section class="header">
            <FieldIconPreview :attribute="clone.attribute" />
            <span class="title">
                {{ $t(`ticimax.attributes.${getAttributeValue()}`) }}
            </span>
            <md-button class="md-icon-button md-mini" @click="onClosed">
                <md-icon>close</md-icon>
            </md-button>
        </section>
        <section class="rules-section">
            <div class="title">{{ $t("chatbotv2.condition") }}</div>
            <div class="rules">
                <div
                    class="rule"
                    v-for="rule of getOperators()"
                    :key="rule.value"
                    :class="{ active: rule.value === clone.rule }"
                    @click="() => changeRule(rule.value)"
                >
                    <font-awesome-icon icon="equals" v-if="rule.value === 'equal'" />
                    <font-awesome-icon icon="not-equal" v-else-if="rule.value === 'not_equal'" />
                    <font-awesome-icon icon="less-than" v-else-if="rule.value === 'less_than'" />
                    <font-awesome-icon icon="less-than-equal" v-else-if="rule.value === 'less_or_equal'" />
                    <font-awesome-icon icon="greater-than" v-else-if="rule.value === 'greater_than'" />
                    <font-awesome-icon icon="greater-than-equal" v-else-if="rule.value === 'greater_or_equal'" />
                    {{ $t(`chatbotv2.filters.${rule.value}`) }}
                </div>
            </div>
        </section>

        <section class="value-section" v-if="!['empty', 'non_empty'].includes(clone.rule)">
            <div class="title">{{ $t("chatbotv2.value") }}</div>
            <input
                v-if="isNumericAttribute()"
                required="required"
                type="number"
                :placeholder="$t('chatbotv2.value')"
                :min="-9007199254740991"
                :max="9007199254740991"
                :step="1"
                :class="{ error: isValueValid(), focused: inputFocused }"
                v-model="clone.value"
                @focus="inputFocused = true"
                @blue="inputFocused = false"
            />
            <span v-else-if="isBoolean()">
                <a-select
                    :value="clone.value + ''"
                    @change="
                        (e) => {
                            clone.value = e;
                        }
                    "
                    class="select-attribute"
                >
                    <a-select-option
                        :value="booleanValue.value + ''"
                        :key="`${booleanValue.label}-${booleanValue.value}`"
                        v-for="booleanValue of getBooleanValues()"
                        >{{ booleanValue.label }}
                    </a-select-option>
                </a-select>
            </span>
            <span v-else-if="isSelect()">
                <a-select
                    :value="clone.value"
                    @change="
                        (e) => {
                            clone.value = e;
                        }
                    "
                    class="select-attribute"
                >
                    <a-select-option :value="opt.value" :key="opt.value" v-for="opt of getOptions()"
                        >{{ opt.label }}
                    </a-select-option>
                </a-select>
            </span>

            <input
                v-else
                required="required"
                type="text"
                :placeholder="$t('chatbotv2.value')"
                :class="{ error: isValueValid(), focused: inputFocused }"
                v-model="clone.value"
                @focus="inputFocused = true"
                @blue="inputFocused = false"
            />
        </section>
        <section class="apply">
            <a-button
                type="primary"
                :disabled="!['empty', 'non_empty'].includes(clone.rule) && isValueValid()"
                @click="submit"
            >
                {{ $t("chatbotv2.applyFilter") }}
            </a-button>
        </section>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import _ from "lodash";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faEquals,
    faGreaterThan,
    faGreaterThanEqual,
    faLessThan,
    faLessThanEqual,
    faNotEqual,
} from "@fortawesome/free-solid-svg-icons";
import CONFIG from "../../../../../../../config/config";
import { mapActions, mapState } from "vuex";
import FieldIconPreview from "./FieldIconPreview.vue";
import TicimaxAttributes from "./TicimaxAttributes";
import filterOperators from "../../../../../Chatbot/utils/filterOperators";

library.add(faEquals, faNotEqual, faLessThan, faLessThanEqual, faGreaterThan, faGreaterThanEqual);
const log = log2("SingleFilterEditor");
export default {
    name: "SingleFilterEditor",
    components: {
        FieldIconPreview,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                rule: "greater_or_equal",
                attribute: "email",
                value: 0,
            }),
        },
        onClosed: {
            type: Function,
            required: true,
        },
        types: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            inputFocused: false,
            SystemAttributeValues: [],
            window,
        };
    },
    computed: {
        ...mapState({}),
    },
    methods: {
        ...mapActions({}),

        changeRule(rule) {
            this.clone.rule = rule;
        },
        onValueChanged(e) {
            log("onValueChanged", e.target.value);
            this.clone.value = e.target.value;
        },
        getAttributeValue() {
            log("getAttributeValue", this.value.attribute);
            return this.value.attribute;
        },
        getAttributeMeta() {
            return TicimaxAttributes(this.types).find((x) => x.value === this.value.attribute);
        },
        getOperatorName() {
            const foundOperator = filterOperators.find((x) => x.value === this.value.rule);
            log("getOperatorName", foundOperator, this.value.rule, filterOperators);
            return _.get(foundOperator, "label", this.value.rule);
        },
        getOperators() {
            const isNumericMode = this.isNumericAttribute();
            const isBoolean = this.isBoolean();
            const meta = this.getAttributeMeta();

            if (isBoolean) {
                return filterOperators.filter((op) => !!op.isBooleanOperator);
            }
            return filterOperators.filter((op) => (isNumericMode ? op.isNumeric : true));
        },
        isNumericAttribute() {
            const numericAttribute = [
                "totalOrderCount",
                "tookDayFromLastOrder",
                "usedCouponCount",
                "usedCouponAmount",
                "totalOrderCount",
                "totalOrderAmount",
                "minOrderAmount",
                "maxOrderAmount",
            ];
            const isNumericMode = numericAttribute.includes(this.clone.attribute);
            return isNumericMode;
        },
        isBoolean() {
            const booleanAttributes = ["vip", "isCouponUser"];
            const isBoolean = booleanAttributes.includes(this.clone.attribute);
            return isBoolean;
        },

        isSelect() {
            return !!TicimaxAttributes(this.types).find(
                (x) => x.value === this.clone.attribute && x.controlType === "select"
            );
        },
        getOptions() {
            const attr = TicimaxAttributes(this.types).find(
                (x) => x.value === this.clone.attribute && x.controlType === "select"
            );

            return _.get(attr, "options", []) || [];
        },
        getBooleanValues() {
            return [
                {
                    label: "True",
                    value: "true",
                },
                {
                    label: "False",
                    value: "false",
                },
            ];
        },
        isValueValid() {
            return ["empty", "non_empty"].includes(this.clone.rule) || _.isEmpty(this.clone.value);
        },
        submit() {
            this.$emit("input", this.clone);
            this.onClosed();
        },
    },
    mounted() {
        log("mounted", this.clone);
    },
    watch: {},
};
</script>

<style lang="scss">
.ticimax-filter-item-editor {
    grid-row-gap: 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    max-width: 90vw;
    padding: 20px;
    width: 380px;

    .select-akinon,
    .select-department,
    .select-channel,
    .select-attribute {
        width: 100%;
    }

    section {
        .title {
            margin-bottom: 5px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }
    }

    section.header {
        align-items: center;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 15px;

        .title {
            margin-bottom: 0px;
        }

        .material-icons {
            color: #006cff;
            font-size: 25px;
            margin-right: 10px;
        }
    }

    section.rules-section {
        .rules {
            display: flex;
            flex-wrap: wrap;
            margin: -5px;

            .rule {
                &.active,
                &:hover {
                    background: #006cff;
                    color: #fff;
                    cursor: default;
                }

                align-items: center;
                background: #f0f4f7;
                border-radius: 6px;
                display: flex;
                font-size: 15px;
                font-weight: 600;
                line-height: 19px;
                margin: 5px;
                padding: 10px 15px;
                transition: all 0.3s;
                cursor: pointer;

                i,
                svg {
                    font-size: 10px;
                    margin-right: 7px;
                }
            }
        }
    }

    section.value-section {
        input {
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            color: #22272d;
            display: block;
            font-family: Source Sans Pro, sans-serif;
            font-weight: 400;
            height: 37px;
            line-height: 35px;
            padding: 0 15px;
            transition: border 0.3s;
            width: 100%;
            font-size: 15px;

            &.error {
                border-color: #fb6e6e;
            }

            &.focused,
            &:hover {
                border-color: #008aff;
            }
        }
    }

    section.apply {
        .ant-btn {
            min-width: 80px;
            color: #fff;
            height: 37px;
            line-height: 35px;
            width: 100%;
            border-radius: 4px;
            display: inline-block;
            font-family: Source Sans Pro, sans-serif;
            font-weight: 600;
            padding: 0 10px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s;

            &[disabled] {
                background: #f5f8fb;
                border: 1px solid #f5f8fb;
                color: #919ead;
                cursor: not-allowed;
            }
        }
    }
}
</style>
